import { useCallback } from 'react';
import { useLanguage } from './useLanguage';
import { translate, type Language } from '../lib/i18n';
import { getLocaleLanguage, isPublicLocale } from '../lib/locales';

interface Translatable {
  name?: string;
  description?: string | null;
  translations?: Record<string, { name?: string; description?: string | null }> | null;
}

export const useTranslation = () => {
  const { currentLanguage, currentLocale, setLanguage, setPublicLocale } = useLanguage();

  const effectiveLanguage = (currentLocale && isPublicLocale(currentLocale) 
    ? getLocaleLanguage(currentLocale) 
    : currentLanguage) as Language;

  const t = useCallback((
    objOrKey: Translatable | string | null | undefined, 
    fieldOrCallback: 'name' | 'description' | string = 'name'
  ): string => {
    if (!objOrKey) return '';

    // Handle static translation keys
    if (typeof objOrKey === 'string') {
      return translate(effectiveLanguage, objOrKey, fieldOrCallback as string);
    }

    // Handle Translatable objects
    const obj = objOrKey as Translatable;
    const field = fieldOrCallback as 'name' | 'description';
    const rootValue = obj[field] ? (obj[field] as string) : '';

    if (!obj.translations) {
      return rootValue;
    }

    // 1. Exact locale match (e.g. 'fr-gb', 'fr-ma')
    const localeTranslation = currentLocale ? obj.translations[currentLocale] : undefined;
    if (localeTranslation && localeTranslation[field]) {
      const val = String(localeTranslation[field]).trim();
      if (val && (effectiveLanguage === 'en' || val !== rootValue)) {
        return val;
      }
    }

    // 2. Base language match (e.g. 'fr', 'ar', 'es')
    const baseTranslation = obj.translations[effectiveLanguage];
    if (baseTranslation && baseTranslation[field]) {
      const baseVal = String(baseTranslation[field]).trim();
      if (baseVal) {
        return baseVal;
      }
    }

    // 3. Regional variant fallback for the same language
    for (const [key, trans] of Object.entries(obj.translations)) {
      if (key.startsWith(`${effectiveLanguage}-`) && trans && trans[field]) {
        const variantVal = String(trans[field]).trim();
        if (variantVal && (effectiveLanguage === 'en' || variantVal !== rootValue)) {
          return variantVal;
        }
      }
    }

    // 4. Return exact translation even if it matches rootValue
    if (localeTranslation && localeTranslation[field]) {
      const val = String(localeTranslation[field]).trim();
      if (val) return val;
    }

    // Fallback to root field if translation missing
    return rootValue;
  }, [effectiveLanguage, currentLocale]);

  return { t, currentLanguage: effectiveLanguage, effectiveLanguage, currentLocale, setLanguage, setPublicLocale };
};
